import { Card, IconButton, LuIcon, Spinner, stopPropagation, Text } from "@overmap-ai/blocks"
import { type GeoImage } from "@overmap-ai/common"
import { selectGeoImageById } from "@overmap-ai/core"
import { ImageFile, useImageViewer } from "@overmap-ai/forms"
import { cx } from "class-variance-authority"
import { type ComponentProps, memo, type MouseEventHandler, useCallback } from "react"

import { useFetchFileFromFileModel } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { GeoImageDropdownMenu } from "../DropdownMenus/GeoImageDropdownMenu"
import { GeoImageIndexLabel } from "../IndexLabels/GeoImageIndex"

interface GeoImageCardProps extends ComponentProps<typeof Card> {
	geoImageId: GeoImage["uuid"]
}

export const GeoImageCard = memo<GeoImageCardProps>((props) => {
	const { geoImageId, className, onClick, ...rest } = props

	const geoImage = useAppSelector(selectGeoImageById(geoImageId))

	if (!geoImage) {
		throw new Error(`GeoImage with id ${geoImageId} not found in store.`)
	}

	const openImageViewer = useImageViewer()

	const [file, error] = useFetchFileFromFileModel(geoImage, sdk.geoImages)

	const handleClick = useCallback<MouseEventHandler<HTMLDivElement>>(
		(e) => {
			onClick?.(e)

			if (!file) return

			openImageViewer((close) => ({
				file,
				onClose: close,
			}))
		},
		[file, onClick, openImageViewer],
	)

	return (
		<Card className={cx("p-0! relative flex flex-col overflow-hidden", className)} onClick={handleClick} {...rest}>
			<div className="flex grow items-center justify-center overflow-hidden bg-clip-padding">
				{!!file && !error ? (
					<ImageFile className="max-w-full object-cover" file={file} />
				) : !error ? (
					<Spinner />
				) : (
					<LuIcon icon={modelIconMapping.alertCircle} />
				)}
			</div>
			<div className="bg-(--base-a2) flex h-max w-full shrink-0 justify-between gap-2 p-4">
				<div className="flex min-w-0 flex-col gap-0">
					<div className="flex grow items-center gap-1">
						<GeoImageIndexLabel geoImageId={geoImage.uuid} className="text-(--base-a11) text-sm" />
						<Text size="sm" className="truncate">
							{geoImage.title}
						</Text>
					</div>

					{!!geoImage.original_date && (
						<Text size="sm" accentColor="base">
							{new Date(geoImage.original_date).toLocaleString(undefined, {
								month: "long",
								day: "numeric",
								year: "numeric",
								hour: "numeric",
								minute: "numeric",
							})}
						</Text>
					)}
				</div>

				<GeoImageDropdownMenu geoImageId={geoImage.uuid} modal size="sm">
					<IconButton size="sm" variant="ghost" accentColor="base" onClick={stopPropagation}>
						<LuIcon icon={modelIconMapping.more} />
					</IconButton>
				</GeoImageDropdownMenu>
			</div>
		</Card>
	)
})
GeoImageCard.displayName = "GeoImageCard"
